import Intl from '@fx-ui/intl';
import { extend, forEach, isArray, isEmpty, size, isObjectEmpty, map, omit } from '@libs/shared';
import { Message, ToastType, TreeEventDataNode } from '@fx-ui/fine-design';

/**
 * 复制链接至剪贴板
 *
 * @param url
 * @param copySuccessMsg
 */
export const copyUrl = (url: string, copySuccessMsg?: string) => {
    if (url && url.length) {
        const textarea = document.createElement('textarea');
        textarea.style.background = 'transparent';
        textarea.style.color = 'transparent';
        textarea.value = url;
        document.body.appendChild(textarea);
        textarea.select();
        let success;
        try {
            success = document.execCommand('copy');
        } catch (err) {
            success = false;
        }
        document.body.removeChild(textarea);
        if (success) {
            const props = {
                messageType: 'toast' as 'toast',
                message: copySuccessMsg ? copySuccessMsg : Intl.get('link.copy.success'),
                type: 'success' as ToastType
            };
            Message.add(props);
        }
    }
};

/**
 * 数组换位
 *
 * @param array
 * @param from
 * @param to
 */
export const arrayMove = <T extends any>(array: T[], from: number, to: number): T[] => {
    const newArray = [...array];
    const startIndex = from < 0 ? newArray.length + from : from;

    if (startIndex >= 0 && startIndex < newArray.length) {
        const endIndex = to < 0 ? newArray.length + to : to;

        const [item] = newArray.splice(from, 1);
        newArray.splice(endIndex, 0, item!);
    }
    return newArray;
};

/**
 * 文件是否为空
 *
 * @static
 * @param obj
 * @returns {boolean} 返回是否为空
 */
export const isFileEmpty = (obj: any) => {
    let result = true;
    if (!isObjectEmpty(obj) || obj instanceof File) {
        result = false;
    }
    return result;
};

/**
 * 数组转树结构
 *
 * @param data
 * @param setting
 * @param setting.idKey
 * @param setting.pIdKey
 * @param setting.children
 * @param setting.title
 */
export const transformToTreeData = <T extends any>(
    data: T[],
    setting?: {
        idKey?: keyof T;
        pIdKey?: keyof T;
        children?: keyof T;
        title?: keyof T;
        extraModifier?: (item: T) => Partial<TreeEventDataNode>;
    }
) => {
    const result: any = [];
    const mDataSetting = {
        idKey: 'departmentId',
        pIdKey: 'parentId',
        children: 'children',
        title: 'name',
        extraModifier: () => {},
        ...setting
    };
    const key = mDataSetting.idKey;
    const parentKey = mDataSetting.pIdKey;
    const childrenKey = mDataSetting.children;
    const titleKey = mDataSetting.title;
    const extraModifier = mDataSetting.extraModifier;
    if (!key || !isArray(data)) return [];
    const nodes: any = [];
    forEach(data, (item: any) => {
        nodes.push(extend({ key: item[key], title: item[titleKey] }, item, extraModifier(item)));
    });
    const tmpMap: any = {};
    for (const n of nodes) {
        tmpMap[n[key]] = n;
    }
    for (const n of nodes) {
        const p = tmpMap[n[parentKey]];
        if (p && n[key] !== n[parentKey]) {
            if (!p[childrenKey]) {
                p[childrenKey] = [];
            }
            const children = p[childrenKey];
            children.push(n);
        } else {
            result.push(n);
        }
    }
    return result;
};

/**
 * 最大长度提示
 *
 * @param maxLength
 */
export const showMaxLengthTip = (maxLength: number) => {
    Message.add({
        messageType: 'toast',
        type: 'warning',
        message: Intl.get('max.length.error.message', { max: maxLength })
    });
};

/**
 * 获取指定位数的随机码
 *
 * @param length 位数
 * @param range 可选范围
 * @returns {string}
 */
export const getRandomCode = (length: number, range?: string) => {
    const randomArr = range || '1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let randomStr = '';
    while (length > 0) {
        const index = Math.floor(Math.random() * randomArr.length);
        randomStr += randomArr[index];
        length--;
    }
    return randomStr;
};

/**
 * 异步队列控制
 *
 * @param syncLimit 异步请求数量上限
 * @param promiseList 异步操作列表
 */
export const promiseQueue = (syncLimit: number, promiseList: (() => Promise<any>)[]) => {
    const queue: Promise<any>[] = []; // 请求队列
    const postQueue: Promise<any>[] = []; // 并发列表

    if (isEmpty(promiseList)) {
        return Promise.resolve();
    }
    const list = promiseList.slice();
    const syncPost: any = () => {
        if (isEmpty(list)) {
            return Promise.resolve();
        }
        const promiseItem = list.pop();
        const postPromise = Promise.resolve().then(() => promiseItem && promiseItem());
        // 放入promises数组
        queue.push(postPromise);
        // promise执行完毕，从postQueue数组中删除
        const executePromise: Promise<any> = postPromise.finally(() => {
            postQueue.splice(postQueue.indexOf(executePromise), 1);
        });
        // 插入executePromise，表示正在执行的promise
        postQueue.push(executePromise);
        // 使用Promise.race，每当executing数组中promise数量低于syncLimit，就实例化新的promise并执行
        let race = Promise.resolve();
        if (size(postQueue) >= syncLimit) {
            race = Promise.race(postQueue);
        }
        // 递归，直到遍历完postItemList
        return race.then(syncPost);
    };
    return syncPost().then(() => Promise.all(queue));
};

/**
 * 根据表格总宽度计算每列的实际宽度：固定宽度列保持不变，
 * 其余空间按各列的最小宽度比例分配，且分配后不会小于最小宽度。
 *
 * @param columns 列配置，width 表示固定宽度，minWidth 表示参与自适应分配的最小宽度
 * @param tableWidth 表格可用的总宽度
 * @returns 将 width 替换为实际宽度后的新列配置
 */
export const calcTableColumnWidth = (
    columns: { width?: number; minWidth?: number; [key: string]: any }[],
    tableWidth: number
) => {
    let sumWidth = 0;
    let sumMinWidth = 0;
    forEach(columns, (column) => {
        if (column.width) {
            sumWidth += column.width;
        } else if (column.minWidth) {
            sumMinWidth += column.minWidth;
        }
    });
    const containerWidth = tableWidth - sumWidth;
    return map(columns, (column) => {
        const { width, minWidth } = column;
        let actualWidth = 0;
        if (width) {
            actualWidth = width;
        } else if (minWidth) {
            actualWidth = Math.max(minWidth, (minWidth / sumMinWidth) * containerWidth);
        }
        return {
            ...omit(column, ['width', 'minWidth']),
            width: actualWidth
        };
    });
};
